import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Plus, Trash2, GripVertical, ChevronUp, ChevronDown, Save,
  RotateCcw, Bot, Play, AlertTriangle, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_BASE } from '@/lib/api';

const AGENT_OPTIONS = [
  { value: 'data_manager', label: 'Data Manager', emoji: '📊', color: 'text-emerald-400' },
  { value: 'nca_agent', label: 'NCA Agent', emoji: '🧪', color: 'text-blue-400' },
  { value: 'modeler_manager', label: 'Modeler Manager', emoji: '🔀', color: 'text-violet-400' },
  { value: 'poppk_expert', label: 'PopPK Expert', emoji: '🧬', color: 'text-purple-400' },
  { value: 'pkpd_expert', label: 'PKPD Expert', emoji: '💊', color: 'text-indigo-400' },
  { value: 'er_expert', label: 'E-R Expert', emoji: '📈', color: 'text-teal-400' },
  { value: 'pbpk_agent', label: 'PBPK Agent', emoji: '🫁', color: 'text-cyan-400' },
  { value: 'statistical_agent', label: 'Statistical Agent', emoji: '📉', color: 'text-amber-400' },
  { value: 'simulator_agent', label: 'Simulator Agent', emoji: '🎲', color: 'text-orange-400' },
  { value: 'qc_agent', label: 'QC Agent', emoji: '✅', color: 'text-red-400' },
  { value: 'report_agent', label: 'Report Agent', emoji: '📄', color: 'text-emerald-400' },
  { value: 'reg_intel_agent', label: 'Regulatory Intel', emoji: '📜', color: 'text-sky-400' },
];

const ERROR_OPTIONS = [
  { value: 'retry', label: 'Retry (up to 3×)' },
  { value: 'skip', label: 'Skip & continue' },
  { value: 'abort', label: 'Abort workflow' },
];

function StepCard({ step, index, onUpdate, onDelete, onMove, totalSteps }) {
  const agent = AGENT_OPTIONS.find(a => a.value === step.agent) || AGENT_OPTIONS[0];

  return (
    <motion.div layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
      <div className="flex gap-2 group">
        {/* Reorder */}
        <div className="flex flex-col items-center gap-0.5 pt-3 opacity-40 group-hover:opacity-100 transition-opacity">
          <button onClick={() => onMove(index, 'up')} disabled={index === 0}><ChevronUp className="w-3.5 h-3.5" /></button>
          <GripVertical className="w-4 h-4 text-muted-foreground/30 cursor-grab" />
          <button onClick={() => onMove(index, 'down')} disabled={index >= totalSteps - 1}><ChevronDown className="w-3.5 h-3.5" /></button>
        </div>

        {/* Step content */}
        <Card className="flex-1 card-hover">
          <CardContent className="py-3 px-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold">{index + 1}</span>
                <span className={`text-sm ${agent.color}`}>{agent.emoji} {agent.label}</span>
                {step.human_review && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-400">🔶 Review</span>
                )}
              </div>
              <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100" onClick={() => onDelete(index)}>
                <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
              </Button>
            </div>

            {/* Fields */}
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px]">Agent</Label>
                  <Select value={step.agent} onValueChange={v => onUpdate(index, 'agent', v)}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {AGENT_OPTIONS.map(a => (
                        <SelectItem key={a.value} value={a.value}>
                          <span className={a.color}>{a.emoji} {a.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px]">On Error</Label>
                  <Select value={step.on_error || 'retry'} onValueChange={v => onUpdate(index, 'on_error', v)}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ERROR_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-[10px]">Action / Instructions</Label>
                <Textarea value={step.action || step.description || ''} rows={2}
                  onChange={e => onUpdate(index, 'action', e.target.value)}
                  className="text-xs resize-none" placeholder="What should this agent do?" />
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch checked={!!step.human_review} onCheckedChange={v => onUpdate(index, 'human_review', v)} />
                  <Label className="text-[10px]">Human Review Gate</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={!!step.requires_dataset} onCheckedChange={v => onUpdate(index, 'requires_dataset', v)} />
                  <Label className="text-[10px]">Requires Dataset</Label>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </motion.div>
  );
}

export default function TemplateEditor({ template, onSave, onClose }) {
  const [name, setName] = useState(template?.name || '');
  const [description, setDescription] = useState(template?.description || '');
  const [steps, setSteps] = useState(template?.steps || []);
  const [saving, setSaving] = useState(false);

  const addStep = () => {
    setSteps(prev => [...prev, {
      agent: 'data_manager', action: '', description: 'New step',
      human_review: false, requires_dataset: false, on_error: 'retry',
    }]);
  };

  const updateStep = (index, field, value) => {
    setSteps(prev => prev.map((s, i) => i === index ? { ...s, [field]: value } : s));
  };

  const deleteStep = (index) => {
    if (!window.confirm('Delete this step?')) return;
    setSteps(prev => prev.filter((_, i) => i !== index));
  };

  const moveStep = (index, direction) => {
    const newIdx = direction === 'up' ? index - 1 : index + 1;
    if (newIdx < 0 || newIdx >= steps.length) return;
    const copy = [...steps];
    [copy[index], copy[newIdx]] = [copy[newIdx], copy[index]];
    setSteps(copy);
  };

  const handleSave = async () => {
    if (!template?.id) return;
    setSaving(true);
    try {
      await axios.put(`${API_BASE}/workflow-templates/${template.id}`, {
        name, description, steps,
      });
      toast.success('Template saved');
      onSave?.({ ...template, name, description, steps });
    } catch (e) {
      toast.error('Save failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!template?.id || !template?.forked_from) return;
    if (!window.confirm('Reset to default? Your customizations will be lost.')) return;
    try {
      await axios.post(`${API_BASE}/workflow-templates/${template.id}/reset`);
      toast.success('Reset to default');
      onClose?.();
    } catch (e) {
      toast.error('Reset failed');
    }
  };

  return (
    <div className="space-y-4">
      {/* Template info */}
      <div className="space-y-3">
        <div>
          <Label className="text-xs">Template Name</Label>
          <Input value={name} onChange={e => setName(e.target.value)} className="h-9 text-sm" />
        </div>
        <div>
          <Label className="text-xs">Description</Label>
          <Textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} className="text-xs" />
        </div>
      </div>

      {/* Steps */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk' }}>
            Workflow Steps ({steps.length})
          </h3>
          <Button size="sm" variant="outline" onClick={addStep} className="rounded-full h-7 text-xs px-3">
            <Plus className="w-3 h-3 mr-1" /> Add Step
          </Button>
        </div>

        <div className="space-y-2">
          {steps.map((step, i) => (
            <StepCard key={i} step={step} index={i}
              onUpdate={updateStep} onDelete={deleteStep}
              onMove={moveStep} totalSteps={steps.length} />
          ))}
          {steps.length === 0 && (
            <div className="text-center py-8 text-sm text-muted-foreground">
              No steps yet. Click "Add Step" to build your workflow.
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-3 border-t border-border">
        {template?.forked_from && (
          <Button variant="ghost" size="sm" onClick={handleReset} className="text-xs text-muted-foreground">
            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset to Default
          </Button>
        )}
        <div className="flex gap-2 ml-auto">
          {onClose && <Button variant="outline" size="sm" onClick={onClose} className="rounded-full text-xs">Cancel</Button>}
          <Button size="sm" onClick={handleSave} disabled={saving} className="rounded-full text-xs">
            <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Saving...' : 'Save Template'}
          </Button>
        </div>
      </div>
    </div>
  );
}
